import {
  createRouter,
  createWebHistory,
  RouteRecordRaw,
  RouteLocationNormalized,
} from 'vue-router';
import HomeView from '../views/HomeView.vue';
import ClientsView from '../views/ClientsView.vue';
import CalculationLayout from '../layouts/CalculationLayout.vue';
import NewClientView from '../views/NewClientView.vue';
import ClientView from '../views/ClientView.vue';
import NewCalculationView from '../views/NewCalculationView.vue';
import SelectSegmentView from '../views/SelectSegmentView.vue';
import CalculationItemsView from '../views/CalculationItemsView.vue';
import CalculationSummaryView from '../views/CalculationSummaryView.vue';
import ErrorView from '../views/ErrorView.vue';
import LoginView from '../views/LoginView.vue';
import CalculationsView from '../views/CalculationsView.vue';
import CalculatorSelectView from '../views/CalculatorSelectView.vue';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'CalculatorSelectView',
    component: CalculatorSelectView,
    meta: {
      layout: 'AuthorizedLayout',
      title: 'Strona główna',
    },
  },

  {
    path: '/:calculatorId',
    name: 'HomeView',
    component: HomeView,
    meta: {
      layout: 'AuthorizedLayout',
      title: 'Strona główna',
      breadcrumb: () => [
        {
          label: 'Strona główna',
          url: '/',
        },
      ],
    },
  },

  {
    path: '/:calculatorId/clients',
    name: 'ClientsView',
    component: ClientsView,
    meta: {
      layout: 'AuthorizedLayout',
      title: 'Moi klienci',
      breadcrumb: (route: RouteLocationNormalized) => [
        {
          label: 'Strona główna',
          url: `/${route.params.calculatorId}`,
        },
        {
          label: 'Moi klienci',
          url: `/${route.params.calculatorId}/clients`,
        },
      ],
    },
  },

  {
    path: '/:calculatorId/calculations',
    name: 'CalculationsView',
    component: CalculationsView,
    meta: {
      layout: 'AuthorizedLayout',
      title: 'Moje kalkulacje',
      breadcrumb: (route: RouteLocationNormalized) => [
        {
          label: 'Strona główna',
          url: `/${route.params.calculatorId}`,
        },
        {
          label: 'Moje kalkulacje',
          url: `/${route.params.calculatorId}/calculations`,
        },
      ],
    },
  },

  {
    path: '/:calculatorId/client',
    name: 'CalculationLayout',
    redirect: '/:calculatorId/client/new', // Use the correct path with calculatorId
    component: CalculationLayout,
    meta: {
      layout: 'AuthorizedLayout',
    },
    children: [
      {
        path: 'new',
        name: 'NewClientView',
        component: NewClientView,
        meta: {
          title: 'Dodaj klienta',
          breadcrumb: (route: RouteLocationNormalized) => [
            {
              label: 'Strona główna',
              url: `/${route.params.calculatorId}`,
            },
            {
              label: 'Moi klienci',
              url: `/${route.params.calculatorId}/clients`,
            },
            {
              label: 'Dodaj klienta',
              url: `/${route.params.calculatorId}/client/new`,
            },
          ],
        },
      },

      {
        path: ':clientId',
        name: 'ClientView',
        component: ClientView,
        meta: {
          title: 'Edytuj klienta',
          breadcrumb: (route: RouteLocationNormalized) => [
            { label: 'Strona główna', url: `/${route.params.calculatorId}` },
            { label: 'Moi klienci', url: `/${route.params.calculatorId}/clients` },
            {
              label: `Klient ${route.params.clientId}`,
              url: `/${route.params.calculatorId}/client/${route.params.clientId}`,
              type: 'client',
            },
          ],
        },
      },

      {
        path: ':clientId/calculation/new',
        name: 'NewCalculationView',
        component: NewCalculationView,
        meta: {
          title: 'Dodaj nową kalkulację',
          breadcrumb: (route: RouteLocationNormalized) => [
            { label: 'Strona główna', url: `/${route.params.calculatorId}` },
            { label: 'Moi klienci', url: `/${route.params.calculatorId}/clients` },
            {
              label: `Klient ${route.params.clientId}`,
              url: `/${route.params.calculatorId}/client/${route.params.clientId}`,
              type: 'client',
            },
            {
              label: `Dodaj kalkulację`,
              url: `/${route.params.calculatorId}/client/${route.params.clientId}/calculation/new`,
            },
          ],
        },
      },

      {
        path: ':clientId/calculation/:calculationId/segment',
        name: 'SelectSegmentView',
        component: SelectSegmentView,
        meta: {
          title: 'Dodaj nową technologię do kalkulacji',
          breadcrumb: (route: RouteLocationNormalized) => [
            { label: 'Strona główna', url: `/${route.params.calculatorId}` },
            { label: 'Moi klienci', url: `/${route.params.calculatorId}/clients` },
            {
              label: `Klient ${route.params.clientId}`,
              url: `/${route.params.calculatorId}/client/${route.params.clientId}`,
              type: 'client',
            },
            {
              label: `Kalkulacja ${route.params.calculationId}`,
              url: `/${route.params.calculatorId}/client/${route.params.clientId}/calculation/${route.params.calculationId}`,
              type: 'calculation',
            },
            {
              label: `Wybierz technologię`,
              url: `/${route.params.calculatorId}/client/${route.params.clientId}/calculation/${route.params.calculationId}/segment`,
            },
          ],
        },
      },

      {
        path: ':clientId/calculation/:calculationId/segment/:segmentId/group/:groupId?/view/:viewId',
        name: 'CalculationViewItems',
        component: CalculationItemsView,
        meta: {
          title: 'Dodaj nową technologię do kalkulacji',
          breadcrumb: (route: RouteLocationNormalized) => [
            { label: 'Strona główna', url: `/${route.params.calculatorId}` },
            { label: 'Moi klienci', url: `/${route.params.calculatorId}/clients` },
            {
              label: `Klient ${route.params.clientId}`,
              url: `/${route.params.calculatorId}/client/${route.params.clientId}`,
              type: 'client',
            },
            {
              label: `Kalkulacja ${route.params.calculationId}`,
              url: `/${route.params.calculatorId}/client/${route.params.clientId}/calculation/${route.params.calculationId}`,
              type: 'calculation',
            },
            {
              label: `Technologia ${route.params.segmentId}`,
              url: `/${route.params.calculatorId}/client/${route.params.clientId}/calculation/${route.params.calculationId}/segment`,
            },
            {
              label: `Wybierz produkt`,
              url: `/${route.params.calculatorId}/client/${route.params.clientId}/calculation/${route.params.calculationId}/segment/${route.params.segmentId}/group/${route.params.groupId}/view/${route.params.viewId}`,
            },
          ],
        },
      },

      {
        path: ':clientId/calculation/:calculationId',
        name: 'CalculationSummaryView',
        component: CalculationSummaryView,
        meta: {
          title: 'Podsumowanie kalkulacji',
          breadcrumb: (route: RouteLocationNormalized) => [
            { label: 'Strona główna', url: `/${route.params.calculatorId}` },
            { label: 'Moi klienci', url: `/${route.params.calculatorId}/clients` },
            {
              label: `Klient ${route.params.clientId}`,
              url: `/${route.params.calculatorId}/client/${route.params.clientId}`,
              type: 'client',
            },
            {
              label: `Podsumowanie kalkulacji`,
              url: `/${route.params.calculatorId}/client/${route.params.clientId}/calculation/${route.params.calculationId}`,
            },
          ],
        },
      },
    ],
  },

  {
    path: '/:calculatorId/error/:status',
    name: 'ErrorView',
    component: ErrorView,
    meta: {
      layout: 'AuthorizedLayout',
    },
  },

  {
    path: '/login',
    name: 'LoginView',
    component: LoginView,
    meta: {
      layout: 'GuestLayout',
    },
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

export default router;
